/**
 * Settings Tab - Delivery config, field mappings
 * UX Redesign: Checkbox-based dual destinations (AND-mode) with SP create/update
 */
import { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Database,
  Plus,
  RefreshCw,
  Save,
  TestTube,
  Activity,
  CreditCard,
  Table2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ToggleGrid } from '@/components/ui/toggle-grid';
import { useDeliveryConfig } from '../hooks/useDeliveryConfig';
import { CrmCardPreview } from './CrmCardPreview';
import { SkeletonForm } from '@/components/ui/skeleton';
import type { DeliveryConfig } from '../types';

// Map field names to i18n keys for human-readable labels
const FIELD_LABEL_KEYS: Record<string, string> = {
  qualityScore: 'embedded.qualityScoreField',
  transcript: 'embedded.transcriptField',
  summary: 'embedded.keyMomentsField',
  saleProbability: 'embedded.saleProbabilityField',
};

export function SettingsTab() {
  const { t } = useTranslation(['settings', 'dashboard', 'common']);
  const {
    config,
    loading,
    error,
    smartProcesses,
    fieldStatuses,
    fieldStatusesKnown,
    fieldStatusesError,
    recentDeliveries,
    isDirty,
    canSave,
    hasDestination,
    saving,
    testing,
    creatingFields,
    saveStatus,
    testStatus,
    testMessage,
    toggleField,
    toggleCrmCard,
    toggleSmartProcess,
    updateSmartProcessId,
    createSmartProcess,
    handleCreateFields,
    handleSave,
    handleTest,
    refetch,
    refetchFieldStatuses,
  } = useDeliveryConfig();

  const [creatingSP, setCreatingSP] = useState(false);

  // Count fields that need to be created. With the statuses unreadable every
  // switched-on field counts: creating is idempotent, so offering it is the repair.
  const fieldsToCreate = fieldStatuses.filter(
    (f) => config.fields[f.name as keyof DeliveryConfig['fields']] && !f.created
  ).length;

  // Validation
  const hasSelectedFields = Object.values(config.fields).some((v) => v);
  const isSmartProcessValid = !config.deliverToSmartProcess || !!config.smartProcessId;

  const canTest = hasSelectedFields && hasDestination && isSmartProcessValid;

  // Validation messages with i18n
  const getValidationMessage = (): string | null => {
    if (!hasDestination) {
      return t(
        'validation.selectAtLeastOneDestination',
        'Please select at least one delivery destination'
      );
    }
    if (!hasSelectedFields) {
      return t(
        'validation.selectAtLeastOneField',
        'Please select at least one field to sync'
      );
    }
    if (config.deliverToSmartProcess && !config.smartProcessId) {
      return t('validation.selectSmartProcess', 'Please select a Smart Process');
    }
    if (fieldStatusesKnown && config.deliverToCrmCard && fieldsToCreate > 0) {
      return t('validation.fieldsNeedCreation');
    }
    return null;
  };

  const validationMessage = getValidationMessage();

  // Handle SP creation
  const handleCreateSmartProcess = async () => {
    setCreatingSP(true);
    try {
      await createSmartProcess();
    } catch {
      // Error already logged in hook
    } finally {
      setCreatingSP(false);
    }
  };

  // Loading state
  if (loading) {
    return <SkeletonForm fields={3} />;
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <AlertTriangle className="h-8 w-8 text-danger mb-3" />
        <p className="text-sm text-danger font-medium mb-2">
          {t('embedded.failedToLoadSettings')}
        </p>
        <p className="text-xs text-fg-muted mb-4">{error}</p>
        <Button onClick={refetch} size="sm" variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('embedded.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Unsaved Changes Indicator */}
      {isDirty && (
        <div className="flex items-center gap-2 text-xs text-warning-fg bg-warning-dim px-3 py-2 rounded-lg">
          <AlertTriangle className="h-4 w-4" />
          {t('embedded.unsavedChanges')}
        </div>
      )}

      {/* Validation Warning */}
      {validationMessage && (
        <div className="flex items-center gap-2 text-xs text-info-fg bg-info-dim px-3 py-2 rounded-lg">
          <AlertTriangle className="h-4 w-4" />
          {validationMessage}
        </div>
      )}

      {/* Delivery Destinations - Checkbox Cards */}
      <div className="bg-bg-surface rounded-lg border border-bg-elevated p-4">
        <h3 className="text-sm font-medium text-fg mb-4">
          {t('embedded.whereToDeliver')}
        </h3>

        <ToggleGrid>
          {/* CRM Card Destination */}
          <div className="rounded-lg border border-border bg-bg-elevated p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={config.deliverToCrmCard}
                onChange={toggleCrmCard}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
              />
              <CreditCard
                className={cn(
                  'h-5 w-5',
                  config.deliverToCrmCard ? 'text-primary' : 'text-fg-muted'
                )}
              />
              <span className="text-sm font-medium text-fg">
                {t('embedded.toCrmCard')}
              </span>
              <span
                className={`ml-auto text-xs ${config.deliverToCrmCard ? 'text-success' : 'text-fg-muted'}`}
              >
                {config.deliverToCrmCard
                  ? t('common.enabled', 'Enabled')
                  : t('common.disabled', 'Disabled')}
              </span>
            </label>
            {config.deliverToCrmCard && (
              <p className="mt-2 ml-12 text-xs text-fg-muted">
                {t('embedded.toCrmCardDescription')}
              </p>
            )}
          </div>

          {/* Smart Process Destination */}
          <div className="rounded-lg border border-border bg-bg-elevated p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={config.deliverToSmartProcess}
                onChange={toggleSmartProcess}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
              />
              <Table2
                className={cn(
                  'h-5 w-5',
                  config.deliverToSmartProcess ? 'text-primary' : 'text-fg-muted'
                )}
              />
              <span className="text-sm font-medium text-fg">
                {t('embedded.toSmartProcess')}
              </span>
              <span
                className={`ml-auto text-xs ${config.deliverToSmartProcess ? 'text-success' : 'text-fg-muted'}`}
              >
                {config.deliverToSmartProcess
                  ? t('common.enabled', 'Enabled')
                  : t('common.disabled', 'Disabled')}
              </span>
            </label>

            {config.deliverToSmartProcess && (
              <div className="mt-3 ml-12 space-y-3">
                {smartProcesses.length > 0 ? (
                  <>
                    <Select
                      value={config.smartProcessId}
                      onValueChange={(val) => updateSmartProcessId(val as string)}
                      placeholder={t('embedded.selectSmartProcessPlaceholder')}
                      options={smartProcesses.map((sp) => ({
                        value: sp.id,
                        label: `${sp.title} (ID: ${sp.entityTypeId})`,
                      }))}
                      size="md"
                    />
                    {config.smartProcessId && (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-success" />
                        <span className="text-xs text-fg-muted">
                          {t('embedded.smartProcessActive', 'Active')}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-fg-muted">
                      {t('embedded.noSmartProcessesFound')}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCreateSmartProcess}
                      disabled={creatingSP}
                      className="text-xs"
                    >
                      {creatingSP ? (
                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <Plus className="h-3 w-3 mr-1" />
                      )}
                      {t('embedded.createSmartProcess', 'Create Smart Process')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </ToggleGrid>
      </div>

      {/* Field Configuration */}
      <div className="bg-bg-surface rounded-lg border border-bg-elevated p-4">
        {fieldStatusesError && (
          <div className="mb-4 flex items-center gap-2 rounded-lg bg-warning-dim px-3 py-2 text-xs text-warning-fg">
            <AlertTriangle className="h-4 w-4" />
            <span className="flex-1">{t('embedded.fieldStatusesUnavailable')}</span>
            <Button size="sm" variant="ghost" onClick={refetchFieldStatuses}>
              <RefreshCw className="h-3 w-3 mr-1" />
              {t('embedded.retry')}
            </Button>
          </div>
        )}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-fg">{t('embedded.whatToShow')}</h3>
          {config.deliverToCrmCard && fieldsToCreate > 0 && (
            <Button
              size="md"
              variant="outline"
              onClick={handleCreateFields}
              disabled={creatingFields}
              className="text-xs"
            >
              {creatingFields ? (
                <>
                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                  {t('embedded.creatingFields')}
                </>
              ) : (
                <>
                  <Database className="h-3 w-3 mr-1" />
                  {t('embedded.createFields', { count: fieldsToCreate })}
                </>
              )}
            </Button>
          )}
        </div>

        <ToggleGrid className="gap-2">
          {fieldStatuses.map((field) => {
            const isEnabled = config.fields[field.name as keyof DeliveryConfig['fields']];
            const labelKey = FIELD_LABEL_KEYS[field.name] || field.label;
            const displayLabel = labelKey.startsWith('embedded.')
              ? t(labelKey)
              : field.label;
            return (
              <div
                key={field.name}
                className={cn(
                  'flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all',
                  'hover:bg-bg-surface',
                  isEnabled && 'bg-primary-dim'
                )}
              >
                <div className="flex items-center gap-3">
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={() =>
                      toggleField(field.name as keyof DeliveryConfig['fields'])
                    }
                    size="sm"
                    aria-label={displayLabel}
                  />
                  <span
                    className={cn('text-sm', isEnabled ? 'text-fg' : 'text-fg-muted')}
                  >
                    {displayLabel}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {fieldStatusesKnown &&
                    config.deliverToCrmCard &&
                    isEnabled &&
                    (field.created ? (
                      <span className="flex items-center gap-1 text-micro text-success">
                        <CheckCircle className="h-3 w-3" />
                        {t('embedded.fieldCreated')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-micro text-warning">
                        <AlertTriangle className="h-3 w-3" />
                        {t('embedded.fieldNotCreated')}
                      </span>
                    ))}
                  {isEnabled && config.deliverToSmartProcess && (
                    <CheckCircle className="h-4 w-4 text-success" />
                  )}
                </div>
              </div>
            );
          })}
        </ToggleGrid>
      </div>

      {/* CRM Card Preview */}
      {config.deliverToCrmCard && hasSelectedFields && (
        <CrmCardPreview
          enabledFields={{
            qualityScore: config.fields.qualityScore,
            transcript: config.fields.transcript,
            summary: config.fields.summary,
            saleProbability: config.fields.saleProbability,
          }}
        />
      )}

      {/* Actions */}
      <div className="mt-4 pt-4 border-t border-border flex gap-2">
        <Button
          onClick={handleSave}
          isLoading={saving}
          disabled={saving || !canSave}
          size="sm"
          title={
            !canSave && !saving
              ? t('fixValidationToSave', 'Fix validation errors to save')
              : undefined
          }
        >
          {!saving && <Save className="h-4 w-4 mr-2" />}
          {t('embedded.saveSettings')}
        </Button>
        <Button
          variant="outline"
          onClick={handleTest}
          disabled={testing || !canTest}
          title={
            !canTest && !testing
              ? t('fixValidationToTest', 'Fix validation errors to test')
              : t('testBitrix24Connection', 'Test Bitrix24 connection')
          }
        >
          {testing ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <TestTube className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Status Messages */}
      {saveStatus === 'success' && (
        <div className="flex items-center gap-2 text-xs text-success-fg bg-success-dim px-3 py-2 rounded-lg">
          <CheckCircle className="h-4 w-4" />
          {t('embedded.configSaved')}
        </div>
      )}
      {saveStatus === 'error' && (
        <div className="flex items-center gap-2 text-xs text-danger-fg bg-danger-dim px-3 py-2 rounded-lg">
          <AlertTriangle className="h-4 w-4" />
          {t('embedded.configSaveFailed')}
        </div>
      )}
      {testStatus === 'success' && (
        <div className="flex items-center gap-2 text-xs text-success-fg bg-success-dim px-3 py-2 rounded-lg">
          <CheckCircle className="h-4 w-4" />
          {testMessage}
        </div>
      )}
      {testStatus === 'error' && (
        <div className="flex items-center gap-2 text-xs text-danger-fg bg-danger-dim px-3 py-2 rounded-lg">
          <AlertTriangle className="h-4 w-4" />
          {testMessage || t('embedded.testFailed')}
        </div>
      )}

      {/* Recent Deliveries */}
      {recentDeliveries.length > 0 && (
        <div className="bg-bg-surface rounded-lg border border-bg-elevated p-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold text-fg-secondary uppercase tracking-wide">
              {t('embedded.recentDeliveries')}
            </span>
          </div>

          <div className="space-y-2">
            {recentDeliveries.map((delivery) => (
              <div
                key={delivery.id}
                className="flex items-center justify-between py-2 border-b border-border last:border-0"
              >
                <div className="flex items-center gap-2">
                  {delivery.status === 'success' && (
                    <CheckCircle className="h-3 w-3 text-success" />
                  )}
                  {delivery.status === 'failed' && (
                    <AlertTriangle className="h-3 w-3 text-danger" />
                  )}
                  {delivery.status === 'pending' && (
                    <RefreshCw className="h-3 w-3 text-info animate-spin" />
                  )}
                  <span className="text-xs text-fg font-mono">{delivery.recordId}</span>
                </div>
                <div className="flex items-center gap-2">
                  {delivery.error && (
                    <span
                      className="text-micro text-danger truncate max-w-[100px]"
                      title={delivery.error}
                    >
                      {delivery.error}
                    </span>
                  )}
                  <span className="text-micro text-fg-muted">{delivery.timestamp}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
